import { createApi } from '@reduxjs/toolkit/query/react';
import { UserDto } from '../../models/user.dto';
import { baseQueryWithRedirect } from '../base-query-with-redirect';
import {setLanguageToLocalStorage} from '../../translations/langUtils';
import i18n from '../../services/i18n';

const userResponseHandler = async (response) => {
   const user = await response.json();
   setLanguageToLocalStorage(user?.language)
   i18n.changeLanguage(user?.language);
   return user;
}

export const userApi = createApi({
   reducerPath: 'user/api',
   baseQuery: baseQueryWithRedirect,
   tagTypes: ['User'],
   endpoints: (build) => ({
      getUserData: build.query<UserDto, void>({
         query() {
            return {
               url: 'user',
               responseHandler: userResponseHandler
            };
         },
         providesTags: ['User']
      }),
      getAllUserProfiles: build.query<UserDto[], void>({
         query() {
            return {
               url: 'user/all',
               method: 'GET'
            };
         },
         providesTags: ['User']
      }),
      updateUserData: build.mutation<UserDto, UserDto>({
         query: (user: UserDto) => ({
            url: 'user',
            method: 'PUT',
            body: user 
         }),
         invalidatesTags: ['User']
      }),
      updateUserRoleToAdmin: build.mutation<UserDto, string>({
         query: (userEmail: string) => ({
            url: `user/${userEmail}/admin`,
            method: 'PUT'
         }),
         invalidatesTags: ['User']
      }),
      deleteUser: build.mutation<void, string>({
         query: (userEmail: string) => ({
            url: `user/${encodeURIComponent(userEmail)}`,
            method: 'DELETE'
         }),
         invalidatesTags: (_result, error) => error ? [] : ['User']
      }),
      changeUserPassword: build.mutation<void, { newPassword: string }>({
         query: ({ newPassword }) => ({
            url: 'user/change-password',
            method: 'PUT',
            body: { newPassword },
         }),
         invalidatesTags: ['User']
      }),
      updateInvoiceTutorialVersion: build.mutation<UserDto, { version: number }>({
         query: ({ version }) => ({
            url: 'user/tutorial/invoice-overview',
            method: 'PUT',
            body: { version },
         }),
         invalidatesTags: ['User']
      })
   }),
});

export const {
   useGetUserDataQuery,
   useGetAllUserProfilesQuery,
   useLazyGetAllUserProfilesQuery,
   useUpdateUserDataMutation,
   useUpdateUserRoleToAdminMutation,
   useDeleteUserMutation,
   useChangeUserPasswordMutation,
   useUpdateInvoiceTutorialVersionMutation
} = userApi;
